import React from 'react';
import { Box, Button, ButtonProps, Typography } from '@mui/material';
import { ReactComponent as AIIcon } from '../../static/images/svg/ri_ai.svg';
import i18n from '../../i18n';

/**
 * 智能分析按钮属性。
 */
interface SmartAnalysisButtonProps extends Omit<ButtonProps, 'children'> {
    /** 按钮文案。 */
    label?: string;
}

/**
 * 智能分析按钮（与风控页设计一致）。
 * @param props - 按钮属性
 * @returns 智能分析按钮
 */
const SmartAnalysisButton = React.forwardRef<HTMLButtonElement, SmartAnalysisButtonProps>(({
    label,
    sx,
    ...restProps
}, ref) => {
    /** 默认按钮文案。 */
    const defaultLabel = i18n.t('intelligent_risk_control_page_text_data_intelligence_analysis');

    return (
        <Button
            ref={ref}
            sx={[
                {
                    alignItems: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 0 4px 0 var(--shadow-50)',
                    color: 'var(--fill-neutral-100)',
                    display: 'inline-flex',
                    gap: '8px',
                    minHeight: 56,
                    padding: '16px',
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    backgroundImage:
                        'linear-gradient(103.4deg, var(--icon-primary-100) 0%, var(--icon-primary-300) 100%)',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        backgroundImage:
                            'linear-gradient(103.4deg, var(--icon-primary-100) 0%, var(--icon-primary-300) 100%)',
                        boxShadow: '0 0 4px 0 var(--shadow-50)',
                        filter: 'brightness(0.96)',
                    },
                    '&.Mui-disabled': {
                        color: 'var(--fill-neutral-100)',
                        opacity: 0.48,
                        backgroundImage:
                            'linear-gradient(103.4deg, var(--icon-primary-100) 0%, var(--icon-primary-300) 100%)',
                    },
                },
                ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
            ]}
            {...restProps}
        >
            <Box
                aria-hidden
                sx={{
                    color: 'inherit',
                    display: 'inline-flex',
                    height: 24,
                    width: 24,
                    '& svg path': { fill: 'currentColor' },
                }}
            >
                <AIIcon />
            </Box>
            <Typography color='inherit' variant='body1'>
                {label || defaultLabel}
            </Typography>
        </Button>
    );
});

SmartAnalysisButton.displayName = 'SmartAnalysisButton';

export default SmartAnalysisButton;
